/**
 * <x-complementary-grid>
 *
 * Card-grid mode of the "Complementary products" block (product-info.liquid).
 * Handles the two card interactions that need JS:
 *
 * - Image click ([data-card-zoom]) opens a PhotoSwipe lightbox with ALL of
 *   the product's images (embedded as JSON per card), configured identically
 *   to the product page gallery zoom (see ProductGallery in theme.js) so both
 *   lightboxes look and paginate the same.
 * - In checkbox mode, clicking anywhere on the card that is not the title
 *   link, the image or the checkbox itself toggles the card's
 *   .complementary-checkbox — the existing <complementary-checkbox-handler>
 *   then adds the checked products to the cart with the main product.
 *
 * The element arrives via the client-side recommendations fetch
 * (<product-recommendations> injects parsed HTML, so this module is loaded
 * from the block markup outside of it and the element upgrades on insertion).
 */

import { PhotoSwipeLightbox } from 'vendor';

class ComplementaryGrid extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this._onClick.bind(this));

    // Scroll mode: size the columns from the VISIBLE row width so exactly
    // --cc-cols cards fit, plus a small peek of the next card when more
    // exist. Computed in JS because the row's nominal width can exceed the
    // viewport on mobile (a known PDP bleed that the global overflow-x
    // guard clips away) — a CSS percentage would size against the clipped,
    // too-wide box, cutting off the last visible card.
    this._scroller = this.querySelector('.complementary-grid--scroll');

    if (this._scroller) {
      this._resizeObserver = new ResizeObserver(() => this._sizeColumns());
      this._resizeObserver.observe(this._scroller);
      requestAnimationFrame(() => this._sizeColumns());
    }
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
  }

  _sizeColumns() {
    const scroller = this._scroller;
    const style = getComputedStyle(scroller);
    const columns = parseInt(style.getPropertyValue('--cc-cols')) || 2;
    const gap = parseFloat(style.columnGap) || 0;
    const rect = scroller.getBoundingClientRect();

    if (rect.width === 0) {
      return;
    }

    // Clamp the row's right edge to the viewport — if the row bleeds past
    // it (mobile PDP bleed), only the visible part counts. No assumption
    // about a right-hand inset: on desktop the row sits in the right-hand
    // buybox column and ends well before the viewport edge.
    const viewport = document.documentElement.clientWidth;
    const visible = Math.min(rect.width, viewport - Math.max(0, rect.left));

    const count = scroller.querySelectorAll('.complementary-card').length;
    const peek = count > columns ? Math.min(28, visible * 0.08) : 0;
    const track = (visible - (columns - 1) * gap - peek) / columns;

    if (track > 0) {
      scroller.style.setProperty('--cc-track', `${track}px`);
    } else {
      // Degenerate geometry (hidden/mid-layout) — let the CSS calc() rule.
      scroller.style.removeProperty('--cc-track');
    }
  }

  get photoswipe() {
    if (this._photoswipe) {
      return this._photoswipe;
    }

    // Keep in sync with the ProductGallery photoswipe config in theme.js —
    // the quick-view zoom must look identical to the product gallery zoom.
    const photoswipe = new PhotoSwipeLightbox({
      pswpModule: () => import('photoswipe'),
      bgOpacity: 1,
      maxZoomLevel: 3,
      closeTitle: window.themeVariables.strings.closeGallery,
      zoomTitle: window.themeVariables.strings.zoomGallery,
      errorMsg: window.themeVariables.strings.errorGallery,
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      zoom: false,
      closeSVG: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
        <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>
        <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>
        <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>
      </svg>`
    });

    photoswipe.addFilter('thumbEl', (thumbEl, data) => data['thumbnailElement'] || thumbEl);

    photoswipe.on('uiRegister', () => {
      photoswipe.pswp.ui.registerElement({
        name: 'bottom-bar',
        order: 5,
        appendTo: 'wrapper',
        html: `
          <div class="pagination">
            <button class="pagination__item group" rel="prev">
              <span class="animated-arrow animated-arrow--reverse"></span>
            </button>

            <span class="pagination__current text-sm">
              <span class="pagination__current-page">1</span> / <span class="pagination__page-count"></span>
            </span>

            <button class="pagination__item group" rel="next">
              <span class="animated-arrow"></span>
            </button>
          </div>
        `,
        onInit: (el, pswp) => {
          el.querySelector('.pagination__page-count').innerText = pswp.getNumItems();
          el.querySelector('[rel="prev"]')?.addEventListener('click', () => pswp.prev());
          el.querySelector('[rel="next"]')?.addEventListener('click', () => pswp.next());
          pswp.on('change', () => {
            el.querySelector('.pagination__current-page').innerText = pswp.currIndex + 1;
          });
        }
      });
    });

    photoswipe.init();
    return this._photoswipe = photoswipe;
  }

  _onClick(event) {
    const card = event.target.closest('.complementary-card');

    if (!card || !this.contains(card)) {
      return;
    }

    const zoomTrigger = event.target.closest('[data-card-zoom]');

    if (zoomTrigger) {
      event.preventDefault();
      this._openZoom(card, zoomTrigger);
      return;
    }

    // Checkbox mode: treat the rest of the card as a selection toggle, but
    // leave real interactive elements (title link, checkbox label) alone.
    if (event.target.closest('a[href], label, input, button')) {
      return;
    }

    const checkbox = card.querySelector('.complementary-checkbox');

    if (checkbox) {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  _openZoom(card, zoomTrigger) {
    const mediaScript = card.querySelector('script[data-card-media]');

    if (!mediaScript) {
      return;
    }

    let dataSource;

    try {
      dataSource = JSON.parse(mediaScript.textContent);
    } catch {
      return;
    }

    if (!Array.isArray(dataSource) || dataSource.length === 0) {
      return;
    }

    const thumbImage = zoomTrigger.querySelector('img');

    if (thumbImage) {
      dataSource[0].thumbnailElement = thumbImage;
      dataSource[0].msrc = thumbImage.currentSrc || dataSource[0].msrc;
    }

    this.photoswipe.loadAndOpen(0, dataSource);
  }
}

if (!window.customElements.get('x-complementary-grid')) {
  window.customElements.define('x-complementary-grid', ComplementaryGrid);
}
